import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@roger/ui-components';
import { getQuestionSchemaRogerdocQuestionSchemaGet } from '../../services/api/sdk.gen';
import { client } from '../../services/api';
import { VisitTypeId } from '../../services/api/models/VisitTypeId';
import { OASISSchema, OASISFormData } from './types';
import { OASISQuestionRenderer } from './OASISQuestionRenderer';
import { OASISNavigation } from './OASISNavigation';
import { useOASISFormStore } from '../../store/slices/oasisFormSlice';
import { useDebounce } from '../../hooks/useDebounce';
import {
  getOASISFormData,
  getSubitemClearUpdates,
  initializePseudoQuestionDefaults,
} from './utils';
import { Loader2, Save, Check } from 'lucide-react';

interface OASISFormProps {
  visitId: string;
  visitType: VisitTypeId;
  initialData?: OASISFormData;
  onSave: (data: OASISFormData) => void | Promise<void>;
}

export const OASISForm: React.FC<OASISFormProps> = ({
  visitType,
  initialData,
  onSave,
}) => {
  const {
    schema,
    formData,
    currentCategory,
    isLoading,
    error,
    lastSaved,
    setSchema,
    setFormData,
    setCurrentCategory,
    setIsLoading,
    setError,
    setLastSaved,
  } = useOASISFormStore();

  const [categories, setCategories] = useState<string[]>([]);
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');

  // Save function that wraps the async logic
  const saveFormData = async (data: OASISFormData) => {
    try {
      setSaveStatus('saving');

      // Transform to casting codes format if enabled
      // Pseudo questions (those without casting values) are kept in the UI
      // for data collection but transformed to casting codes for database storage
      const dataToSave = schema ? getOASISFormData(data, schema) : data;

      await onSave(dataToSave);
      setLastSaved(new Date());
      setSaveStatus('saved');

      // Clear "saved" status after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  // Debounced save function (save after 2 seconds of inactivity)
  const debouncedSave = useDebounce((data: OASISFormData) => {
    void saveFormData(data);
  }, 2000);

  // Reset current category when visit type changes
  useEffect(() => {
    setCurrentCategory(null);
  }, [visitType, setCurrentCategory]);

  // Fetch schema on mount or when visit type changes
  useEffect(() => {
    const fetchSchema = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getQuestionSchemaRogerdocQuestionSchemaGet({
          query: { visit_type: visitType },
          client,
        });

        if (response && response.data) {
          const schemaData = response.data as OASISSchema;
          setSchema(schemaData);
          const categoryList = Object.keys(schemaData);
          setCategories(categoryList);
          if (categoryList.length > 0) {
            setCurrentCategory(categoryList[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch question schema:', err);
        setError('Failed to load form schema. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchema();
  }, [visitType, setSchema, setIsLoading, setError, setCurrentCategory]);

  // Initialize form data from saved data with pseudo question defaults
  useEffect(() => {
    if (schema) {
      const dataWithDefaults = initializePseudoQuestionDefaults(
        initialData || {},
        schema
      );
      setFormData(dataWithDefaults);
    }
  }, [initialData, schema, setFormData]);

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
  };

  const handleFormDataChange = (updates: Partial<OASISFormData>) => {
    // Start with current form data
    const newFormData: OASISFormData = { ...formData };

    // Apply updates - undefined values delete the key
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) {
        delete newFormData[key];
      } else {
        newFormData[key] = value;
      }
    });

    setFormData(newFormData);

    // Trigger debounced auto-save
    debouncedSave(newFormData);
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-96'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className='pt-6'>
          <p className='text-red-600'>{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!schema || categories.length === 0) {
    return (
      <Card>
        <CardContent className='pt-6'>
          <p>No form schema available for this visit type.</p>
        </CardContent>
      </Card>
    );
  }

  const currentQuestions = (currentCategory && schema[currentCategory]) || [];

  return (
    <div className='grid grid-cols-12 gap-6'>
      {/* Navigation Sidebar */}
      <div className='col-span-3'>
        <OASISNavigation
          categories={categories}
          currentCategory={currentCategory}
          onCategoryChange={handleCategoryChange}
          schema={schema}
          formData={formData}
        />
      </div>

      {/* Main Form Content */}
      <div className='col-span-9'>
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <CardTitle>{currentCategory}</CardTitle>
              <div className='flex items-center gap-2 text-sm text-gray-500'>
                {saveStatus === 'saving' && (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    <span>Saving...</span>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <Check className='h-4 w-4 text-green-600' />
                    <span className='text-green-600'>Saved</span>
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <Save className='h-4 w-4 text-red-600' />
                    <span className='text-red-600'>Save failed</span>
                  </>
                )}
                {saveStatus === 'idle' && lastSaved && (
                  <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className='space-y-6'>
            {currentQuestions.map(question => (
              <OASISQuestionRenderer
                key={question.qid}
                question={question}
                value={formData[question.qid]}
                onChange={value => {
                  // Create the main update
                  const updates: Partial<OASISFormData> = {
                    [question.qid]: value,
                  };

                  // Add subitem clearing updates
                  const clearUpdates = getSubitemClearUpdates(
                    question,
                    value,
                    formData
                  );
                  Object.assign(updates, clearUpdates);

                  handleFormDataChange(updates);
                }}
                formData={formData}
                onFormDataChange={handleFormDataChange}
              />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
